const ALLOWED_REDIRECT_DOMAINS = [
  ".akitabox.com",
  ".beta-abx.com",
  ".test-abx.com",
];

/**
 * Resolve a candidate redirect target and return the normalized absolute href
 * to navigate to, or `null` if the target is not safe.
 *
 * Resolving before validating is what makes relative destinations safe to
 * check, and they are the common case: both `$location.url()` and
 * `location.pathname` produce relative values. The URL parser applies the same
 * normalization the browser would (backslash to slash, tab/newline stripping),
 * so we inspect exactly the value we are about to navigate to - which is what
 * prevents open-redirect via `/\evil.com` style variants.
 *
 * @param url absolute or relative candidate redirect target
 * @param base absolute URL to resolve relative targets against, e.g.
 *   `window.location.origin`. Omit to require `url` to be absolute.
 */
export function resolveSafeRedirectUrl(
  url: string,
  base?: string
): string | null {
  if (!url) return null;

  let parsed: URL;
  let baseOrigin: string | null = null;
  try {
    parsed = new URL(url, base);
    if (base) baseOrigin = new URL(base).origin;
  } catch {
    return null;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return null;
  }

  // Our own origin is safe by definition, and allowing it is what keeps
  // non-allowlisted development hosts (localhost, 127.0.0.1) working.
  if (baseOrigin && parsed.origin === baseOrigin) {
    return parsed.href;
  }

  const domain = ALLOWED_REDIRECT_DOMAINS.find(
    (allowed) =>
      parsed.hostname === allowed.slice(1) || parsed.hostname.endsWith(allowed)
  );
  if (!domain) return null;

  if (parsed.protocol === "http:") {
    return null;
  }

  return parsed.href;
}

/**
 * Whether `url` is a safe redirect target. See {@link resolveSafeRedirectUrl};
 * prefer that function when you need the value to navigate to, so that the
 * string you validated is the string you use.
 */
export function isSafeRedirectUrl(url: string, base?: string): boolean {
  return resolveSafeRedirectUrl(url, base) !== null;
}
